import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';

import * as api from '../api/client';
import { sourceToCategory } from '../data/category-labels';
import { escapeHtml, catalogHitDescription } from '../lib/cells';

/**
 * 컬럼 정의.
 *
 * type 별 동작:
 *   text                 일반 input
 *   number               숫자 input (양수만)
 *   dropdown             <select> — options 필수
 *   option-part-search   AutocompleteInput 동작 — /api/admin/search 호출,
 *                        선택 시 같은 행의 autoCategoryColumn 도 자동 채움
 *   toggle               on/off 스위치 — boolean 값. focus/space/click 토글.
 *                        키보드 ↑↓←→ 셀 이동, Space/Enter 토글.
 *   readonly             표시 전용 — 사용자 입력 불가. compute(row) 로 값 계산.
 *                        선택/포커스 가능하지만 편집/paste 안 됨.
 */
export interface DataSheetColumn<TRow> {
  key: keyof TRow & string;
  label: string;
  type: 'text' | 'number' | 'dropdown' | 'option-part-search' | 'toggle' | 'readonly';
  width?: number;
  options?: ReadonlyArray<{ value: string; label: string }>;
  align?: 'left' | 'center' | 'right';
  placeholder?: string;
  /**
   * `readonly` 컬럼 전용 — 셀에 표시할 값을 행에서 계산.
   * 미지정 시 row[key] 그대로 표기.
   */
  compute?: (row: TRow) => string;
}

export interface DataSheetOptions<TRow> {
  columns: ReadonlyArray<DataSheetColumn<TRow>>;
  initialRows?: TRow[];
  /** 빈 행 제외한 rows 변경 시 호출 (저장 버튼 활성화 등) */
  onChange?: (rows: TRow[]) => void;
  /**
   * option-part-search 컬럼에서 항목 선택 시 같은 행의 어떤 컬럼에 category 를 채울지.
   * default: 'category' (해당 키의 컬럼이 있으면 자동 채움)
   */
  autoCategoryColumn?: string;
  /**
   * option-part-search 컬럼에서 항목 선택 시 검색 결과의 extra_description 을
   * 어떤 컬럼에 채울지. default: 'item_description'.
   * extra_description 이 비어있으면 채우지 않음 (기존 입력 보존).
   */
  autoDescriptionColumn?: string;
  /**
   * "의미 있는 행" 판정 기준 컬럼 목록.
   * 이 컬럼 중 하나라도 값 있으면 not-empty → 마지막 빈 행 자동 추가.
   * 미지정 시 모든 컬럼 검사 (기존 동작).
   *
   * 예: lead_item 은 ['option_part', 'item_description'] — 카테고리/수량만 있는 행은
   * 빈 행으로 간주 (DDL 의 chk_lead_item_has_identifier 와 정렬).
   */
  identifyingColumnKeys?: ReadonlyArray<string>;
}

interface DataSheetData {
  // 활성 셀 (= 영역 선택의 anchor 반대편 = selEnd)
  activeRow: number;
  activeCol: number;
  // 영역 선택 anchor (Shift+화살표 시작점) — 보통 activeRow/Col 과 동일
  selStartRow: number;
  selStartCol: number;
  /**
   * 편집 모드 (엑셀 패턴):
   *   false (navigation): 화살표 = 셀 이동
   *   true  (editing):    화살표 ←→ = cursor 이동, ↑↓ Enter Esc 셀 이동/종료
   * typing 시 자동 true, F2 토글, Enter/Esc/셀 이동 시 false
   */
  isEditing: boolean;
  // option-part-search 활성 listbox 상태
  searchOpen: boolean;
  searchLoading: boolean;
  searchHighlightIdx: number;
}

/**
 * 🎯 DataSheet<TRow>
 *
 * 스프레드시트 같은 UX. lead_item / RFQ Tier 1 등 다중 행 입력에 사용.
 *
 * 사용 예:
 *   const sheet = new DataSheet<LeadItemDraft>({
 *     columns: [
 *       { key: 'category', label: '카테고리', type: 'dropdown', options: CATEGORY_OPTIONS },
 *       { key: 'option_part', label: 'option_part / 설명', type: 'option-part-search' },
 *       { key: 'quantity', label: '수량', type: 'number', width: 80 },
 *       { key: 'notes', label: '메모', type: 'text', width: 200 },
 *     ],
 *     onChange: rows => view.setItems(rows),
 *   });
 *
 * 키 동작:
 *   ↑↓←→ Tab Enter   셀 이동
 *   Enter (마지막 빈 행) 새 행 자동 추가
 *   ※ paste/Delete/Backspace 는 sub-step 6~8 에서 추가
 */
export class DataSheet<TRow extends Record<string, unknown>>
  extends SolaView<DataSheetData> {
  private rows: TRow[] = [];
  private readonly columns: ReadonlyArray<DataSheetColumn<TRow>>;
  private readonly autoCategoryColumn: string;
  private readonly autoDescriptionColumn: string;
  private readonly identifyingKeys: ReadonlyArray<string> | null;
  private readonly opts: DataSheetOptions<TRow>;

  // option-part-search 결과 캐시 (시트 단위로 1개)
  private searchItems: api.CatalogSearchHit[] = [];
  private searchTimer?: ReturnType<typeof setTimeout>;
  private searchAbort?: AbortController;
  private searchBlurTimer?: ReturnType<typeof setTimeout>;

  /**
   * option-part-search listbox 의 DOM element.
   * document.body 에 직접 추가 — ancestor 의 transform/filter 등 영향 안 받게.
   * onMount 에서 생성, onUnmount 에서 제거.
   */
  private searchListboxEl: HTMLDivElement | null = null;

  /**
   * autocomplete 로 의식적 선택된 행만 매칭됨(✓) 표시.
   * key = rowIdx (행 추가/삭제 시 재정렬 필요)
   */
  private matchedRows = new Map<number, string>();

  override events = {
    input: {
      '@cell-text': 'handleCellTextInput',
      '@cell-search': 'handleCellSearchInput',
    },
    change: {
      '@cell-dropdown': 'handleCellDropdownChange',
    },
    keydown: {
      '@cell-text': 'handleCellKeydown',
      '@cell-search': 'handleCellSearchKeydown',
      '@cell-dropdown': 'handleCellKeydown',
      '@cell-toggle': 'handleCellToggleKeydown',
      '@cell-readonly': 'handleCellReadonlyKeydown',
    },
    // focus/blur 는 bubble 안 함 → focusin/focusout (bubble 됨) 사용해야
    // Sola 의 root 이벤트 위임이 작동한다.
    focusin: {
      '@cell-text': 'handleCellFocus',
      '@cell-search': 'handleCellFocus',
      '@cell-dropdown': 'handleCellFocus',
      '@cell-toggle': 'handleCellFocus',
      '@cell-readonly': 'handleCellFocus',
    },
    focusout: {
      '@cell-search': 'handleCellSearchBlur',
    },
    click: {
      '@row-delete': 'handleRowDelete',
      '@row-add': 'handleRowAdd',
      '@cell-toggle': 'handleCellToggleClick',
    },
    // listbox 이벤트(mousedown/mouseover @search-item)는 onMount 에서
    // body 의 listbox 에 직접 등록 — root 의 자손이 아니므로 위임 불가.
    paste: {
      '@cell-text': 'handleCellPaste',
      '@cell-search': 'handleCellPaste',
    },
  };

  constructor(opts: DataSheetOptions<TRow>) {
    super();
    this.opts = opts;
    this.columns = opts.columns;
    this.autoCategoryColumn = opts.autoCategoryColumn ?? 'category';
    this.autoDescriptionColumn = opts.autoDescriptionColumn ?? 'item_description';
    this.identifyingKeys = opts.identifyingColumnKeys ?? null;

    this.rows = (opts.initialRows ?? []).slice();
    // 초기 빈 행 1개 — 사용자가 입력 시작할 수 있도록 (이후 자동 추가 안 함, + 버튼 또는 paste)
    if (this.rows.length === 0) {
      this.rows.push(this.emptyRow());
    }

    this.data = {
      activeRow: -1,
      activeCol: -1,
      selStartRow: -1,
      selStartCol: -1,
      isEditing: false,
      searchOpen: false,
      searchLoading: false,
      searchHighlightIdx: -1,
    };
  }

  override onMount(): void {
    this.refreshRows();
    // listbox 는 lazy 생성 — 처음 검색 시 ensureSearchListbox 가 만듬
  }

  override onUnmount(): void {
    super.onUnmount();
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (this.searchBlurTimer) clearTimeout(this.searchBlurTimer);
    this.searchAbort?.abort();
    this.unmountSearchListbox();
  }

  /** lazy 생성 — 사용 시점에 처음 만들어짐. */
  private ensureSearchListbox(): HTMLDivElement {
    if (!this.searchListboxEl) this.mountSearchListbox();
    return this.searchListboxEl!;
  }

  /** listbox 를 document.body 에 추가 + 자체 이벤트 등록 (root 위임 안 됨). */
  private mountSearchListbox(): void {
    if (this.searchListboxEl) return;
    const box = document.createElement('div');
    box.className = 'datasheet-search-listbox hidden';
    box.setAttribute('role', 'listbox');

    // listbox 가 root 의 자손이 아니므로 자체 이벤트 등록
    // mousedown 으로 처리 — input blur 보다 먼저 발동, e.preventDefault 로 blur 차단
    box.addEventListener('mousedown', (e) => {
      const target = (e.target as HTMLElement | null)?.closest(
        '[data-action="search-item"]',
      ) as HTMLElement | null;
      if (!target) return;
      e.preventDefault();
      const idx = Number(target.dataset.idx);
      if (!Number.isInteger(idx)) return;
      this.data.searchHighlightIdx = idx;
      this.commitSearchSelection(this.data.activeRow);
    });
    box.addEventListener('mouseover', (e) => {
      const target = (e.target as HTMLElement | null)?.closest(
        '[data-action="search-item"]',
      ) as HTMLElement | null;
      if (!target) return;
      const idx = Number(target.dataset.idx);
      if (!Number.isInteger(idx)) return;
      if (idx !== this.data.searchHighlightIdx) {
        this.data.searchHighlightIdx = idx;
        this.refreshSearchHighlight();
      }
    });

    document.body.appendChild(box);
    this.searchListboxEl = box;
  }

  private unmountSearchListbox(): void {
    if (this.searchListboxEl) {
      this.searchListboxEl.remove();
      this.searchListboxEl = null;
    }
  }

  // ===== 외부 API =====

  /** 빈 행 제외한 rows 반환 (저장 시 사용) */
  public getRows(): TRow[] {
    return this.rows.filter((r) => !this.isRowEmpty(r));
  }

  /** 특정 행 셀 값 설정 (autocomplete 선택 등) */
  public setCell(rowIdx: number, colKey: string, value: unknown): void {
    if (rowIdx < 0 || rowIdx >= this.rows.length) return;
    this.rows[rowIdx] = { ...this.rows[rowIdx], [colKey]: value } as TRow;
    this.refreshRows();
    this.fireOnChange();
  }

  /**
   * 외부에서 행 추가 — 특정 위치에 삽입 (default 마지막).
   *
   * @param afterIdx 이 행 다음에 새 빈 행 삽입. undefined 면 맨 뒤에 추가.
   */
  public addRow(afterIdx?: number): void {
    if (afterIdx == null || afterIdx >= this.rows.length - 1) {
      this.rows.push(this.emptyRow());
    } else {
      const insertAt = afterIdx + 1;
      this.rows.splice(insertAt, 0, this.emptyRow());
      // matchedRows 인덱스 재정렬 (삽입 위치 아래는 +1)
      const newMap = new Map<number, string>();
      for (const [idx, src] of this.matchedRows) {
        if (idx < insertAt) newMap.set(idx, src);
        else newMap.set(idx + 1, src);
      }
      this.matchedRows = newMap;
    }
    this.refreshRows();
    this.fireOnChange();
  }

  /** 외부에서 rows 통째로 교체 (편집 모드 등에서 prefill) */
  public setRows(rows: TRow[]): void {
    this.rows = rows.slice();
    if (this.rows.length === 0) this.rows.push(this.emptyRow());
    this.refreshRows();
  }

  // ===== Render =====

  render(): HTMLElement {
    // listbox 는 onMount 에서 document.body 로 portal — 여기엔 안 둠.
    return htmlSolaWithInstance(this)`
      <div class="datasheet" tabindex="-1">
        <table class="datasheet-table">
          <thead class="datasheet-thead">
            <tr>
              <th class="datasheet-col-num">#</th>
              ${this.renderColHeaders()}
              <th class="datasheet-col-actions"></th>
            </tr>
          </thead>
          <tbody class="datasheet-tbody"></tbody>
        </table>
      </div>
    `;
  }

  private renderColHeaders(): string {
    return this.columns
      .map((col) => {
        const w = col.width ? ` style="width:${col.width}px"` : '';
        return `<th class="datasheet-th"${w}>${escapeHtml(col.label)}</th>`;
      })
      .join('');
  }

  private refreshRows(): void {
    const tbody = this._rootElement?.querySelector('.datasheet-tbody');
    if (!tbody) return;
    tbody.innerHTML = this.rows.map((row, ri) => this.renderRow(row, ri)).join('');
    this.refreshActiveCellHighlight();
  }

  private renderRow(row: TRow, ri: number): string {
    const activeRow = ri === this.data.activeRow ? ' active' : '';
    const cells = this.columns
      .map((col, ci) => this.renderCell(col, row, ri, ci))
      .join('');
    // 행 끝 — × 삭제 + + 추가 버튼
    // (행이 1개면 × 가 시트를 비워버리지 않도록 removeRowAt 가 보장)
    const actionBtns = `
      <td class="datasheet-cell-actions">
        <button type="button" class="datasheet-rowadd-btn"
                data-action="row-add" data-row="${ri}" title="이 행 아래에 새 행 추가">＋</button>
        <button type="button" class="datasheet-rowdelete-btn"
                data-action="row-delete" data-row="${ri}" title="행 삭제">×</button>
      </td>
    `;
    return `
      <tr class="datasheet-row${activeRow}" data-row="${ri}">
        <td class="datasheet-cell-num">${ri + 1}</td>
        ${cells}
        ${actionBtns}
      </tr>
    `;
  }

  private renderCell(
    col: DataSheetColumn<TRow>,
    row: TRow,
    ri: number,
    ci: number,
  ): string {
    const isActive = ri === this.data.activeRow && ci === this.data.activeCol;
    const activeCls = isActive ? ' active' : '';
    const value = row[col.key];
    const valStr = value == null ? '' : String(value);

    if (col.type === 'dropdown') {
      const options = col.options ?? [];
      const opts = options
        .map(
          (o) =>
            `<option value="${escapeHtml(o.value)}"${o.value === valStr ? ' selected' : ''}>${escapeHtml(o.label)}</option>`,
        )
        .join('');
      return `
        <td class="datasheet-cell datasheet-cell-dropdown${activeCls}" data-row="${ri}" data-col="${ci}">
          <select class="datasheet-input" data-action="cell-dropdown"
                  data-row="${ri}" data-col="${ci}">
            <option value=""></option>
            ${opts}
          </select>
        </td>
      `;
    }

    if (col.type === 'option-part-search') {
      const partVal = String(row[col.key] ?? '');
      const matched = this.isOptionPartMatched(row, ri);
      const badge = matched ? '<span class="datasheet-match-ok" title="카탈로그 매칭됨">✓</span>' : '';
      return `
        <td class="datasheet-cell datasheet-cell-search${activeCls}" data-row="${ri}" data-col="${ci}">
          <input type="text" class="datasheet-input" data-action="cell-search"
                 data-row="${ri}" data-col="${ci}"
                 placeholder="${escapeHtml(col.placeholder ?? '검색 또는 직접 입력')}"
                 autocomplete="off" spellcheck="false"
                 value="${escapeHtml(partVal)}" />
          ${badge}
        </td>
      `;
    }

    if (col.type === 'toggle') {
      const on = value === true || value === 'true' || value === 1 || value === '1';
      const ariaChecked = on ? 'true' : 'false';
      return `
        <td class="datasheet-cell datasheet-cell-toggle${activeCls}" data-row="${ri}" data-col="${ci}">
          <button type="button"
                  class="datasheet-toggle${on ? ' on' : ''}"
                  data-action="cell-toggle"
                  data-row="${ri}" data-col="${ci}"
                  role="switch"
                  aria-checked="${ariaChecked}"
                  title="${on ? '켜짐 — 클릭/스페이스로 끄기' : '꺼짐 — 클릭/스페이스로 켜기'}">
            <span class="datasheet-toggle-track"></span>
            <span class="datasheet-toggle-thumb"></span>
          </button>
        </td>
      `;
    }

    if (col.type === 'readonly') {
      const display = col.compute ? col.compute(row) : valStr;
      const alignCls = col.align === 'center' ? ' center' : col.align === 'right' ? ' right' : '';
      return `
        <td class="datasheet-cell datasheet-cell-readonly${activeCls}${alignCls}" data-row="${ri}" data-col="${ci}">
          <div class="datasheet-readonly" tabindex="0"
               data-action="cell-readonly"
               data-row="${ri}" data-col="${ci}">${escapeHtml(display)}</div>
        </td>
      `;
    }

    // text / number
    const inputType = col.type === 'number' ? 'number' : 'text';
    const minAttr = col.type === 'number' ? ' min="0"' : '';
    const alignCls = col.align === 'center' ? ' center' : col.align === 'right' ? ' right' : '';
    return `
      <td class="datasheet-cell datasheet-cell-${col.type}${activeCls}${alignCls}" data-row="${ri}" data-col="${ci}">
        <input type="${inputType}" class="datasheet-input" data-action="cell-text"
               data-row="${ri}" data-col="${ci}"${minAttr}
               placeholder="${escapeHtml(col.placeholder ?? '')}"
               autocomplete="off"
               value="${escapeHtml(valStr)}" />
      </td>
    `;
  }

  // ===== Active cell tracking =====

  /** 활성 셀 설정 + 영역 collapse + 편집 모드 종료. 일반 이동/클릭 시. */
  private setActive(ri: number, ci: number): void {
    this.data.activeRow = ri;
    this.data.activeCol = ci;
    this.data.selStartRow = ri;
    this.data.selStartCol = ci;
    this.data.isEditing = false; // 셀 이동/클릭 시 navigation 모드로 복귀
    this.refreshActiveCellHighlight();
  }

  /** selStart 는 유지, selEnd(=activeRow/Col) 만 이동. Shift+화살표용. */
  private extendSelectionTo(ri: number, ci: number): void {
    this.data.activeRow = ri;
    this.data.activeCol = ci;
    this.refreshActiveCellHighlight();
  }

  /** 현재 영역의 사각 좌표 (정규화: r0≤r1, c0≤c1). 활성 셀 없으면 null. */
  private getSelectionRect(): {
    r0: number;
    c0: number;
    r1: number;
    c1: number;
  } | null {
    if (
      this.data.activeRow < 0 ||
      this.data.activeCol < 0 ||
      this.data.selStartRow < 0 ||
      this.data.selStartCol < 0
    ) {
      return null;
    }
    return {
      r0: Math.min(this.data.selStartRow, this.data.activeRow),
      c0: Math.min(this.data.selStartCol, this.data.activeCol),
      r1: Math.max(this.data.selStartRow, this.data.activeRow),
      c1: Math.max(this.data.selStartCol, this.data.activeCol),
    };
  }

  /** 영역 크기 > 1 (여러 셀 선택 상태)? */
  private hasSelectionRange(): boolean {
    const rect = this.getSelectionRect();
    if (!rect) return false;
    return rect.r0 !== rect.r1 || rect.c0 !== rect.c1;
  }

  private refreshActiveCellHighlight(): void {
    const root = this._rootElement;
    if (!root) return;
    // 기존 표시 제거
    root.querySelectorAll('.datasheet-cell.active, .datasheet-cell.selected').forEach((el) => {
      el.classList.remove('active');
      el.classList.remove('selected');
    });
    root.querySelectorAll('.datasheet-row.active').forEach((el) =>
      el.classList.remove('active'),
    );
    if (this.data.activeRow < 0 || this.data.activeCol < 0) return;

    // 영역 selected 표시
    const rect = this.getSelectionRect();
    if (rect && (rect.r0 !== rect.r1 || rect.c0 !== rect.c1)) {
      for (let r = rect.r0; r <= rect.r1; r++) {
        for (let c = rect.c0; c <= rect.c1; c++) {
          const cell = root.querySelector(
            `.datasheet-cell[data-row="${r}"][data-col="${c}"]`,
          );
          if (cell) cell.classList.add('selected');
        }
      }
    }

    // 활성 셀 outline
    const active = root.querySelector(
      `.datasheet-cell[data-row="${this.data.activeRow}"][data-col="${this.data.activeCol}"]`,
    );
    if (active) {
      active.classList.add('active');
      active.classList.toggle('editing', this.data.isEditing);
    }
    const row = root.querySelector(
      `.datasheet-row[data-row="${this.data.activeRow}"]`,
    );
    if (row) row.classList.add('active');
  }

  /** input element 의 dataset 에서 (row, col) 추출 */
  private parseCellTarget(target: HTMLElement): { row: number; col: number } | null {
    const row = Number(target.dataset.row);
    const col = Number(target.dataset.col);
    if (!Number.isInteger(row) || !Number.isInteger(col)) return null;
    return { row, col };
  }

  // ===== 입력 핸들러 =====

  handleCellFocus(_e: Event, target: HTMLElement): void {
    const pos = this.parseCellTarget(target);
    if (!pos) return;
    this.setActive(pos.row, pos.col);
    // option-part-search 가 아닌 셀에 focus 면 listbox 닫기
    const col = this.columns[pos.col];
    if (col?.type !== 'option-part-search' && this.data.searchOpen) {
      this.closeSearchListbox();
    }
  }

  handleCellTextInput(_e: Event, target: HTMLElement): void {
    const pos = this.parseCellTarget(target);
    if (!pos) return;
    const col = this.columns[pos.col];
    if (!col) return;
    // typing 시작 = 편집 모드 자동 진입
    if (!this.data.isEditing) {
      this.data.isEditing = true;
      this.refreshActiveCellHighlight();
    }
    const input = target as HTMLInputElement;
    let val: unknown = input.value;
    if (col.type === 'number') {
      val = input.value === '' ? null : Number(input.value);
    }
    this.rows[pos.row] = { ...this.rows[pos.row], [col.key]: val } as TRow;
    this.fireOnChange();
  }

  handleCellDropdownChange(_e: Event, target: HTMLElement): void {
    const pos = this.parseCellTarget(target);
    if (!pos) return;
    const col = this.columns[pos.col];
    if (!col) return;
    const select = target as HTMLSelectElement;
    const val = select.value === '' ? null : select.value;
    this.rows[pos.row] = { ...this.rows[pos.row], [col.key]: val } as TRow;
    this.fireOnChange();
  }

  handleCellKeydown(e: Event, target: HTMLElement): void {
    const ev = e as KeyboardEvent;
    const pos = this.parseCellTarget(target);
    if (!pos) return;

    // Ctrl+C / Cmd+C — 영역 있으면 TSV 복사, 없으면 기본 input copy
    if ((ev.ctrlKey || ev.metaKey) && (ev.key === 'c' || ev.key === 'C')) {
      if (this.hasSelectionRange()) {
        ev.preventDefault();
        void this.copySelection();
      }
      return;
    }

    switch (ev.key) {
      case 'F2': {
        // 편집 모드 토글 (엑셀과 동일)
        ev.preventDefault();
        this.data.isEditing = !this.data.isEditing;
        this.refreshActiveCellHighlight();
        if (this.data.isEditing) {
          // cursor 끝으로
          const inp = target as HTMLInputElement;
          try {
            inp.setSelectionRange(inp.value.length, inp.value.length);
          } catch {
            /* number/date 등 selection 미지원 — 무시 */
          }
        }
        break;
      }
      case 'Escape': {
        // 편집 모드 종료 (listbox 우선 처리는 handleCellSearchKeydown 에서)
        if (this.data.isEditing) {
          ev.preventDefault();
          this.data.isEditing = false;
          this.refreshActiveCellHighlight();
        }
        break;
      }
      case 'ArrowDown':
        ev.preventDefault();
        if (ev.shiftKey) this.extendSelectionVert(pos.row + 1, pos.col);
        else this.moveCell(pos.row + 1, pos.col);
        break;
      case 'ArrowUp':
        ev.preventDefault();
        if (ev.shiftKey) this.extendSelectionVert(pos.row - 1, pos.col);
        else this.moveCell(pos.row - 1, pos.col);
        break;
      case 'Enter': {
        ev.preventDefault();
        // 편집 모드면 우선 명시적으로 종료 (셀 이동 실패해도 종료는 보장)
        if (this.data.isEditing) {
          this.data.isEditing = false;
          this.refreshActiveCellHighlight();
        }
        // 다음 행 같은 컬럼으로 이동 (마지막 행이면 이동 X, 편집만 종료된 상태)
        this.moveCell(pos.row + 1, pos.col);
        break;
      }
      case 'Tab':
        ev.preventDefault();
        // 편집 모드 명시 종료 (셀 이동 실패해도 보장)
        if (this.data.isEditing) {
          this.data.isEditing = false;
          this.refreshActiveCellHighlight();
        }
        if (ev.shiftKey) this.moveCellHoriz(pos.row, pos.col, -1);
        else this.moveCellHoriz(pos.row, pos.col, +1);
        break;
      case 'ArrowRight': {
        // Shift+→ : 영역 확장
        if (ev.shiftKey) {
          ev.preventDefault();
          this.extendSelectionHoriz(pos.row, pos.col + 1);
          break;
        }
        // navigation 모드: 무조건 셀 이동
        // editing 모드: cursor 끝일 때만 셀 이동 (text cursor 우선)
        if (!this.data.isEditing) {
          ev.preventDefault();
          this.moveCellHoriz(pos.row, pos.col, +1);
          break;
        }
        const input = target as HTMLInputElement | HTMLSelectElement;
        if (input.tagName === 'SELECT' || this.isCursorAtEnd(input as HTMLInputElement)) {
          ev.preventDefault();
          this.moveCellHoriz(pos.row, pos.col, +1);
        }
        break;
      }
      case 'ArrowLeft': {
        if (ev.shiftKey) {
          ev.preventDefault();
          this.extendSelectionHoriz(pos.row, pos.col - 1);
          break;
        }
        if (!this.data.isEditing) {
          ev.preventDefault();
          this.moveCellHoriz(pos.row, pos.col, -1);
          break;
        }
        const input = target as HTMLInputElement | HTMLSelectElement;
        if (input.tagName === 'SELECT' || this.isCursorAtStart(input as HTMLInputElement)) {
          ev.preventDefault();
          this.moveCellHoriz(pos.row, pos.col, -1);
        }
        break;
      }
      case 'Delete': {
        // 셀 비우기 + focus 유지
        // 단, input cursor 가 텍스트 중간/끝이면 사용자가 글자 지우려는 거라 기본 동작
        const input = target as HTMLInputElement | HTMLSelectElement;
        if (input.tagName === 'SELECT') {
          ev.preventDefault();
          this.clearCell(pos.row, pos.col);
        } else {
          const inp = input as HTMLInputElement;
          // 전체 선택 또는 빈 셀에서만 셀 통째 비우기
          if (inp.value === '' || (inp.selectionStart === 0 && inp.selectionEnd === inp.value.length)) {
            ev.preventDefault();
            this.clearCell(pos.row, pos.col);
          }
          // else: 기본 동작 (한 글자 지우기)
        }
        break;
      }
      case 'Backspace': {
        // 빈 셀 + 첫 컬럼 → 행 삭제 + 위 행 같은 컬럼으로 이동
        const input = target as HTMLInputElement | HTMLSelectElement;
        const inp = input as HTMLInputElement;
        const isEmpty = input.tagName === 'SELECT'
          ? (inp.value === '' || inp.value === null)
          : inp.value === '';
        if (isEmpty && pos.col === 0 && this.rows.length > 1) {
          // 빈 셀 + 첫 컬럼 + 행 2개 이상 → 행 삭제 후 위 행으로
          ev.preventDefault();
          const targetRow = Math.max(0, pos.row - 1);
          this.removeRowAt(pos.row);
          this.focusCell(targetRow, pos.col);
        }
        // else: 기본 동작
        break;
      }
      default:
        break;
    }
  }

  /** 활성 셀 비우기 (값 null, matchedRows 도 그 행이면 해제) */
  private clearCell(row: number, col: number): void {
    const c = this.columns[col];
    if (!c) return;
    this.rows[row] = { ...this.rows[row], [c.key]: null } as TRow;
    // option-part-search 컬럼 비우면 매칭 해제
    if (c.type === 'option-part-search') {
      this.matchedRows.delete(row);
    }
    this.refreshRows();
    this.focusCell(row, col);
    this.fireOnChange();
  }

  // ===== Paste (Sub-step 6) =====

  /**
   * 셀 paste 핸들러. 다중 행/컬럼 (TSV) paste 시 자체 처리, 단일 값은 기본 동작.
   *
   * 엑셀에서 N행 × M컬럼 복사 → 현재 셀 기준 우/하 방향으로 채움.
   * 빈 행 부족하면 자동 추가. 카테고리 dropdown 은 한글 라벨 → key 변환 시도.
   */
  handleCellPaste(e: Event, target: HTMLElement): void {
    const ev = e as ClipboardEvent;
    const pos = this.parseCellTarget(target);
    if (!pos) return;
    const data = ev.clipboardData?.getData('text/plain') ?? '';
    if (!data) return;

    // 다중 행/컬럼 판정 — 줄바꿈 또는 탭 포함 시 자체 처리
    if (!data.includes('\n') && !data.includes('\t')) {
      // 단일 값 — 기본 paste 동작에 맡김 (input 이벤트가 자동으로 호출됨)
      return;
    }

    ev.preventDefault();
    // TSV 파싱
    const lines = data.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    // 마지막 빈 줄 제거 (엑셀이 끝에 \n 붙임)
    while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();
    if (lines.length === 0) return;

    const matrix: string[][] = lines.map((ln) => ln.split('\t'));

    // 시트에 행 부족하면 추가
    const needed = pos.row + matrix.length;
    while (this.rows.length < needed) {
      this.rows.push(this.emptyRow());
    }

    // 각 cell 에 채움
    for (let r = 0; r < matrix.length; r++) {
      const ri = pos.row + r;
      const cells = matrix[r];
      for (let c = 0; c < cells.length; c++) {
        const ci = pos.col + c;
        if (ci >= this.columns.length) break;
        const col = this.columns[ci];
        const rawVal = cells[c];
        const val = this.coerceForColumn(col, rawVal);
        // undefined 는 sentinel — readonly 컬럼 skip
        if (val === undefined) continue;
        this.rows[ri] = { ...this.rows[ri], [col.key]: val } as TRow;
      }
      // paste 된 행의 option_part 는 의식적 선택 아니므로 매칭 표시 해제
      this.matchedRows.delete(ri);
    }

    this.refreshRows();
    this.fireOnChange();

    // paste 끝나면 마지막 paste 된 셀로 포커스
    const lastRow = pos.row + matrix.length - 1;
    const lastCol = Math.min(
      pos.col + (matrix[0]?.length ?? 1) - 1,
      this.columns.length - 1,
    );
    this.focusCell(lastRow, lastCol);
  }

  /**
   * paste 된 raw 텍스트를 컬럼 타입에 맞게 변환.
   *
   * - number: parseFloat, NaN 이면 null
   * - dropdown: value 일치 우선, 아니면 한글 label 매칭 → value, 그래도 안 맞으면 null
   * - text / option-part-search: 그대로
   */
  private coerceForColumn(col: DataSheetColumn<TRow>, raw: string): unknown {
    const trimmed = raw.trim();
    if (trimmed === '') return null;

    if (col.type === 'readonly') {
      // 읽기 전용 — paste 무시 (sentinel: undefined 면 호출부에서 skip)
      return undefined;
    }
    if (col.type === 'toggle') {
      const lower = trimmed.toLowerCase();
      if (['true', '1', 'y', 'yes', 'on', 'o', '예', '켜짐'].includes(lower)) return true;
      if (['false', '0', 'n', 'no', 'off', 'x', '아니오', '꺼짐'].includes(lower)) return false;
      return null;
    }
    if (col.type === 'number') {
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : null;
    }
    if (col.type === 'dropdown') {
      const options = col.options ?? [];
      // 1) value 일치
      const byValue = options.find((o) => o.value === trimmed);
      if (byValue) return byValue.value;
      // 2) label 일치 (한글 등)
      const byLabel = options.find((o) => o.label === trimmed);
      if (byLabel) return byLabel.value;
      // 3) label 부분일치 (대소문자 무시)
      const lower = trimmed.toLowerCase();
      const byLabelLower = options.find((o) => o.label.toLowerCase() === lower);
      if (byLabelLower) return byLabelLower.value;
      return null;
    }
    return trimmed;
  }

  private isCursorAtEnd(input: HTMLInputElement): boolean {
    // type=number, date 등 일부 input 은 selectionStart 가 null — 항상 셀 이동 허용
    if (input.selectionStart === null) return true;
    return input.selectionStart === input.value.length;
  }
  private isCursorAtStart(input: HTMLInputElement): boolean {
    if (input.selectionStart === null) return true;
    return input.selectionStart === 0;
  }

  private moveCell(row: number, col: number): void {
    if (row < 0) return;
    if (row >= this.rows.length) {
      // 마지막 행 아래로 → ensure 가 추가했으므로 그 행으로 이동
      // 이미 보장됨 (마지막 빈 행 유지). row >= length 면 멈춤.
      return;
    }
    if (col < 0 || col >= this.columns.length) return;
    this.focusCell(row, col);
  }

  private moveCellHoriz(row: number, col: number, dir: 1 | -1): void {
    let r = row;
    let c = col + dir;
    if (c >= this.columns.length) {
      r += 1;
      c = 0;
    } else if (c < 0) {
      r -= 1;
      c = this.columns.length - 1;
    }
    if (r < 0 || r >= this.rows.length) return;
    this.focusCell(r, c);
  }

  /**
   * Shift+↑↓ — 같은 컬럼 위/아래로 selEnd 확장. selStart 유지.
   * focus 는 anchor (selStart) 셀에 그대로 — focusCell 옮기면 handleCellFocus 가
   * selStart 를 재설정해버려서 영역이 collapse 됨.
   */
  private extendSelectionVert(row: number, col: number): void {
    if (row < 0 || row >= this.rows.length) return;
    this.extendSelectionTo(row, col);
    // selEnd 셀이 화면 밖이면 보이게
    this.scrollSelEndIntoView(row, col);
  }

  /** Shift+←→ — 같은 행 좌/우로 selEnd 확장. 행 넘김 X. */
  private extendSelectionHoriz(row: number, col: number): void {
    if (col < 0 || col >= this.columns.length) return;
    this.extendSelectionTo(row, col);
    this.scrollSelEndIntoView(row, col);
  }

  private scrollSelEndIntoView(row: number, col: number): void {
    const root = this._rootElement;
    if (!root) return;
    const cell = root.querySelector(
      `.datasheet-cell[data-row="${row}"][data-col="${col}"]`,
    ) as HTMLElement | null;
    cell?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  /**
   * 영역(rect) 을 TSV 문자열로 직렬화 후 클립보드 복사.
   * dropdown 컬럼은 value 그대로 (paste 호환 위해).
   */
  private async copySelection(): Promise<void> {
    const rect = this.getSelectionRect();
    if (!rect) return;
    const lines: string[] = [];
    for (let r = rect.r0; r <= rect.r1; r++) {
      const row = this.rows[r];
      if (!row) continue;
      const cells: string[] = [];
      for (let c = rect.c0; c <= rect.c1; c++) {
        const col = this.columns[c];
        const v = row[col.key];
        cells.push(v == null ? '' : String(v));
      }
      lines.push(cells.join('\t'));
    }
    const tsv = lines.join('\n');
    try {
      await navigator.clipboard.writeText(tsv);
    } catch (err) {
      // 권한/HTTPS 이슈 fallback — execCommand 는 deprecated 지만 비-secure context 에서 작동
      console.warn('[DataSheet] clipboard.writeText 실패, fallback 시도:', err);
      this.fallbackCopy(tsv);
    }
  }

  private fallbackCopy(text: string): void {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
    } catch {
      // 마지막 안전망 — 실패해도 사용자 흐름 방해 안 함
    }
    document.body.removeChild(ta);
  }

  /** 셀에 포커스 옮기기 + active 표시 (navigation 모드 진입). */
  public focusCell(row: number, col: number): void {
    const root = this._rootElement;
    if (!root) return;
    // .datasheet-input (text/number/dropdown/search) 또는
    // .datasheet-toggle (toggle button) 또는
    // .datasheet-readonly (readonly div, tabindex=0) 중 첫 매칭
    const cell = root.querySelector(
      `.datasheet-cell[data-row="${row}"][data-col="${col}"] .datasheet-input,
       .datasheet-cell[data-row="${row}"][data-col="${col}"] .datasheet-toggle,
       .datasheet-cell[data-row="${row}"][data-col="${col}"] .datasheet-readonly`,
    ) as HTMLElement | null;
    this.setActive(row, col); // isEditing=false
    if (cell) {
      cell.focus();
      // navigation 진입 — text input 이면 전체 selection (엑셀처럼 typing 시 대체)
      if (cell.tagName === 'INPUT' && (cell as HTMLInputElement).type === 'text') {
        const inp = cell as HTMLInputElement;
        try {
          inp.select();
        } catch {
          // 일부 환경 미지원 — 무시
        }
      }
    }
  }

  // ===== toggle / readonly 컬럼 =====

  /** 토글 클릭 — 값 반전 + 같은 셀에 포커스 유지 */
  handleCellToggleClick(_e: Event, target: HTMLElement): void {
    const pos = this.parseCellTarget(target);
    if (!pos) return;
    this.toggleBoolAt(pos.row, pos.col);
    this.focusCell(pos.row, pos.col);
  }

  /** 토글 셀 keydown — Space/Enter = 토글, ↑↓←→ Tab = 일반 이동 */
  handleCellToggleKeydown(e: Event, target: HTMLElement): void {
    const ev = e as KeyboardEvent;
    const pos = this.parseCellTarget(target);
    if (!pos) return;

    if (ev.key === ' ' || ev.key === 'Spacebar' || ev.key === 'Enter') {
      ev.preventDefault();
      this.toggleBoolAt(pos.row, pos.col);
      // 토글 후 같은 셀 유지 — 연속 토글 가능
      return;
    }
    // 그 외 키는 일반 셀 키 처리 (화살표 이동, F2, Tab 등)
    this.handleCellKeydown(e, target);
  }

  /** readonly 셀 keydown — 입력 무시, 이동/복사만 허용 */
  handleCellReadonlyKeydown(e: Event, target: HTMLElement): void {
    const ev = e as KeyboardEvent;
    // 텍스트 입력 키는 모두 흡수 — readonly 보장
    // 이동/복사/F2/Esc 만 일반 핸들러로
    if (
      ev.key.length === 1 &&
      !ev.ctrlKey && !ev.metaKey && !ev.altKey
    ) {
      ev.preventDefault();
      return;
    }
    this.handleCellKeydown(e, target);
  }

  /** boolean 값을 반전. matched/onChange 발화. */
  private toggleBoolAt(row: number, col: number): void {
    const c = this.columns[col];
    if (!c) return;
    const cur = this.rows[row][c.key];
    const isOn = cur === true || cur === 'true' || cur === 1 || cur === '1';
    this.rows[row] = { ...this.rows[row], [c.key]: !isOn } as TRow;
    this.refreshRows();
    this.fireOnChange();
  }

  // ===== row 삭제 =====

  handleRowAdd(_e: Event, target: HTMLElement): void {
    const ri = Number(target.dataset.row);
    if (!Number.isInteger(ri)) return;
    this.addRow(ri);
    // 새 행의 첫 셀로 focus
    this.focusCell(ri + 1, 0);
  }

  handleRowDelete(_e: Event, target: HTMLElement): void {
    const ri = Number(target.dataset.row);
    if (!Number.isInteger(ri)) return;
    if (ri < 0 || ri >= this.rows.length) return;
    this.removeRowAt(ri);
  }

  /** 행 splice + matchedRows 인덱스 재정렬 */
  private removeRowAt(ri: number): void {
    this.rows.splice(ri, 1);
    // matchedRows 재정렬: ri 보다 큰 키는 -1, ri 는 삭제
    const newMap = new Map<number, string>();
    for (const [idx, src] of this.matchedRows) {
      if (idx < ri) newMap.set(idx, src);
      else if (idx > ri) newMap.set(idx - 1, src);
    }
    this.matchedRows = newMap;
    // 행이 0개로 줄어들면 빈 행 1개 보장 (시트가 완전히 비어버리지 않게)
    if (this.rows.length === 0) {
      this.rows.push(this.emptyRow());
    }
    this.refreshRows();
    this.fireOnChange();
  }

  // ===== option-part-search =====

  handleCellSearchInput(_e: Event, target: HTMLElement): void {
    const pos = this.parseCellTarget(target);
    if (!pos) return;
    const col = this.columns[pos.col];
    if (!col) return;

    // typing 하는 셀이 곧 활성 셀 — focus 이벤트 누락 안전망
    if (this.data.activeRow !== pos.row || this.data.activeCol !== pos.col) {
      this.setActive(pos.row, pos.col);
    }
    // typing 시작 = 편집 모드 자동 진입
    if (!this.data.isEditing) {
      this.data.isEditing = true;
      this.refreshActiveCellHighlight();
    }

    const q = (target as HTMLInputElement).value;
    this.rows[pos.row] = { ...this.rows[pos.row], [col.key]: q } as TRow;
    this.fireOnChange();

    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (q.trim().length === 0) {
      this.searchAbort?.abort();
      this.searchItems = [];
      this.closeSearchListbox();
      return;
    }

    this.searchTimer = setTimeout(() => {
      this.searchTimer = undefined;
      void this.runSearch(q);
    }, 280);
  }

  handleCellSearchBlur(): void {
    if (this.searchBlurTimer) clearTimeout(this.searchBlurTimer);
    this.searchBlurTimer = setTimeout(() => {
      this.searchBlurTimer = undefined;
      this.closeSearchListbox();
    }, 150);
  }

  handleCellSearchKeydown(e: Event, target: HTMLElement): void {
    const ev = e as KeyboardEvent;
    const pos = this.parseCellTarget(target);
    if (!pos) return;

    // listbox 열려있을 때 ↑↓Enter 우선 처리
    if (this.data.searchOpen && this.searchItems.length > 0) {
      switch (ev.key) {
        case 'ArrowDown':
          ev.preventDefault();
          this.data.searchHighlightIdx = Math.min(
            this.data.searchHighlightIdx + 1,
            this.searchItems.length - 1,
          );
          this.refreshSearchHighlight();
          return;
        case 'ArrowUp':
          ev.preventDefault();
          this.data.searchHighlightIdx = Math.max(
            this.data.searchHighlightIdx - 1,
            0,
          );
          this.refreshSearchHighlight();
          return;
        case 'Enter':
          if (this.data.searchHighlightIdx >= 0) {
            ev.preventDefault();
            this.commitSearchSelection(pos.row);
            return;
          }
          break;
        case 'Escape':
          if (this.data.searchOpen) {
            ev.preventDefault();
            this.closeSearchListbox();
            return;
          }
          break;
        default:
          break;
      }
    }
    // listbox 안 열려있으면 일반 셀 키 동작
    this.handleCellKeydown(e, target);
  }

  // (handleSearchItemMouseDown / handleSearchItemHover 는 listbox 가 body portal 로
  //  옮겨가면서 mountSearchListbox 에서 listbox 자체에 직접 addEventListener 로 등록.
  //  events 객체 위임은 root 자손에만 작동하므로 사용 안 함.)

  private async runSearch(q: string): Promise<void> {
    this.searchAbort?.abort();
    this.searchAbort = new AbortController();
    this.data.searchLoading = true;
    this.openSearchListbox();
    this.refreshSearchListbox();

    try {
      const result = await api.searchAll(q, 0, 10);
      // 이전 요청 폐기는 AbortController 가 처리 (catch 의 AbortError).
      // (별도 activeCellValue 비교는 false positive 발생 — 제거)
      this.searchItems = result.catalog.slice(0, 10);
      this.data.searchHighlightIdx = this.searchItems.length > 0 ? 0 : -1;
      this.data.searchLoading = false;
      this.refreshSearchListbox();
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      console.error('[DataSheet search]', err);
      this.searchItems = [];
      this.data.searchLoading = false;
      this.refreshSearchListbox();
    }
  }

  /** highlighted 항목 선택 → 같은 행의 option_part + category 채움 */
  private commitSearchSelection(rowIdx: number): void {
    if (rowIdx < 0) return;
    const idx = this.data.searchHighlightIdx;
    if (idx < 0 || idx >= this.searchItems.length) return;
    const item = this.searchItems[idx];

    // 1) option-part-search 컬럼 채움
    const partCol = this.columns.find((c) => c.type === 'option-part-search');
    if (partCol) {
      this.rows[rowIdx] = { ...this.rows[rowIdx], [partCol.key]: item.option_part } as TRow;
    }
    // 2) autoCategoryColumn 의 dropdown 컬럼 자동 채움
    const catCol = this.columns.find(
      (c) => c.key === this.autoCategoryColumn && c.type === 'dropdown',
    );
    if (catCol) {
      this.rows[rowIdx] = {
        ...this.rows[rowIdx],
        [catCol.key]: sourceToCategory(item.source),
      } as TRow;
    }
    // 3) autoDescriptionColumn 에 검색 결과의 extra_description 채움
    //    (extra_description 비어있으면 기존 입력 보존)
    const descCol = this.columns.find(
      (c) => c.key === this.autoDescriptionColumn,
    );
    const desc = item.extra_description;
    if (descCol && desc != null && String(desc).trim() !== '') {
      this.rows[rowIdx] = {
        ...this.rows[rowIdx],
        [descCol.key]: String(desc),
      } as TRow;
    }
    // 4) 매칭 표시 기록 (✓ 표시용)
    this.matchedRows.set(rowIdx, item.source);
    this.refreshRows();
    this.closeSearchListbox();
    this.fireOnChange();

    // 다음 셀로 포커스 (UX: 자동완성 끝나면 자연스럽게 다음 입력)
    const colIdx = partCol
      ? this.columns.findIndex((c) => c.key === partCol.key)
      : -1;
    if (colIdx >= 0) {
      this.moveCellHoriz(rowIdx, colIdx, +1);
    }
  }

  private openSearchListbox(): void {
    this.data.searchOpen = true;
  }

  private closeSearchListbox(): void {
    this.data.searchOpen = false;
    if (this.searchListboxEl) this.searchListboxEl.classList.add('hidden');
  }

  private refreshSearchListbox(): void {
    const box = this.ensureSearchListbox();
    if (!this.data.searchOpen) {
      box.classList.add('hidden');
      return;
    }

    // 활성 셀 아래에 위치 (viewport 기준, body 자식이라 ancestor 영향 없음)
    this.positionSearchListbox(box);

    const parts: string[] = [];
    if (this.data.searchLoading) {
      parts.push('<div class="autocomplete-status">검색 중…</div>');
    } else if (this.searchItems.length === 0) {
      parts.push('<div class="autocomplete-status">일치하는 결과가 없습니다.</div>');
    } else {
      this.searchItems.forEach((hit, idx) => {
        const active = idx === this.data.searchHighlightIdx ? ' active' : '';
        const desc = catalogHitDescription(hit);
        const labelHtml = `<strong>${escapeHtml(hit.option_part)}</strong> <span class="ac-dim">[${escapeHtml(hit.source)}]${desc ? ' · ' + escapeHtml(desc) : ''}</span>`;
        parts.push(
          `<div class="autocomplete-item${active}" data-action="search-item" data-idx="${idx}" role="option">${labelHtml}</div>`,
        );
      });
    }
    box.innerHTML = parts.join('');
    box.classList.remove('hidden');
  }

  private refreshSearchHighlight(): void {
    const box = this.ensureSearchListbox();
    box.querySelectorAll('.autocomplete-item').forEach((el, idx) => {
      el.classList.toggle('active', idx === this.data.searchHighlightIdx);
    });
    const active = box.querySelector('.autocomplete-item.active');
    if (active) (active as HTMLElement).scrollIntoView({ block: 'nearest' });
  }

  /**
   * listbox 를 활성 셀 바로 아래로 위치.
   *
   * position: fixed 사용 — viewport 좌표 그대로. ancestor 의 position/transform
   * 영향 받지 않아서 안정적.
   *
   * 화면 우측 끝/하단 가까이면 잘리지 않게 조정.
   */
  private positionSearchListbox(box: HTMLElement): void {
    const root = this._rootElement;
    if (!root) return;

    // 1) 활성 셀로 위치 — 가장 정확
    let cell = root.querySelector(
      `.datasheet-cell[data-row="${this.data.activeRow}"][data-col="${this.data.activeCol}"]`,
    ) as HTMLElement | null;

    // 2) fallback: 활성 셀 못 찾으면 현재 focus 된 input 의 부모 셀
    if (!cell) {
      const focused = document.activeElement as HTMLElement | null;
      if (focused && focused.classList.contains('datasheet-input')) {
        cell = focused.closest('.datasheet-cell') as HTMLElement | null;
      }
    }
    if (!cell) return;

    const cellRect = cell.getBoundingClientRect();
    const width = Math.max(cellRect.width, 320);
    // 우측 끝 잘림 방지 — left 가 viewport 안에 들어오게 clamp
    const maxLeft = Math.max(8, window.innerWidth - width - 8);
    const left = Math.max(8, Math.min(cellRect.left, maxLeft));

    // 아래로 펼치되, 공간 부족하면 셀 위로
    const listMaxH = 280;
    const spaceBelow = window.innerHeight - cellRect.bottom;
    let top = cellRect.bottom + 2;
    if (spaceBelow < listMaxH + 8 && cellRect.top > listMaxH) {
      top = cellRect.top - Math.min(listMaxH, cellRect.top - 8) - 2;
    }

    box.style.left = `${left}px`;
    box.style.top = `${top}px`;
    box.style.width = `${width}px`;
  }

  // ===== Helpers =====

  private emptyRow(): TRow {
    const obj: Record<string, unknown> = {};
    for (const c of this.columns) obj[c.key] = null;
    return obj as TRow;
  }

  private isRowEmpty(row: TRow): boolean {
    if (this.identifyingKeys) {
      // 식별자 컬럼만 검사 — 그 외 컬럼만 채워진 행은 빈 행으로 간주
      for (const k of this.identifyingKeys) {
        const v = row[k];
        if (v != null && v !== '') return false;
      }
      return true;
    }
    for (const c of this.columns) {
      const v = row[c.key];
      if (v != null && v !== '') return false;
    }
    return true;
  }

  /**
   * option_part 가 카탈로그에 매칭된 형태(✓ 표시)인지.
   * autocomplete listbox 에서 의식적 선택했을 때만 true.
   * 자유 텍스트 / paste 는 false (사용자 의도 4번 — 자동 추정 안 함).
   */
  private isOptionPartMatched(_row: TRow, rowIdx: number): boolean {
    return this.matchedRows.has(rowIdx);
  }

  private fireOnChange(): void {
    this.opts.onChange?.(this.getRows());
  }
}
